/**
 * Pure formatting helpers for the Transaction Ledger tables (v2 — DNKADM-0149 U2).
 *
 * Shared by the payments and credits ledger tables (and the screen) so the two stay
 * in lockstep: the row timestamp format (mirrors `ConfigAuditLog`'s `formatTime`) and
 * the token humanizer (`booking_debit` → "Booking debit"). Kept in a `.ts` module
 * (no component exports) so React Fast Refresh stays happy.
 */
import { DATE_TIME, formatInstant } from '@/utils/instantFormat';

/**
 * Format an ISO/epoch instant for a ledger row (e.g. "Jun 10, 9:12 AM") by
 * parsing it as the real UTC instant its `Z` suffix declares and rendering it
 * in `timeZone` (the venue's IANA zone from the location context), falling
 * back to the browser zone when the venue zone is absent or invalid.
 *
 * HISTORY — do not reinstate the render-AS-IS compensation. Until the Azure
 * MySQL migration (Central→UTC DATETIME cutover, 2026-08-14), the ledger's
 * `createdAt` held venue-local wall time mislabelled with a `Z` suffix, so
 * DNKADM-0303 U4 correctly rendered the wire wall clock verbatim — parsing the
 * dishonest label double-converted (1:48 PM CDT displayed as 8:48 AM). The
 * cutover made the stored value genuinely UTC, which turned that compensation
 * into the opposite bug (the same charge displayed as 6:48 PM). The `Z` label
 * is honest now; a normal `Date` parse is correct again (hotfix
 * ledger-time-post-utc-cutover).
 *
 * Falls back to the raw string when unparseable and to an em dash when
 * absent — never throws.
 */
export function formatTransactionTime(ts: string | null, timeZone: string | null = null): string {
  // The shared instant formatter (DNKADM-0476 F-05): an invalid IANA name degrades, never throws.
  return formatInstant(ts, timeZone, DATE_TIME);
}

/** Humanize a snake/kebab token, e.g. `booking_debit` → "Booking debit". */
export function humanizeToken(token: string): string {
  const spaced = token.replace(/[_-]+/g, ' ').trim();
  if (spaced.length === 0) return token;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}
